import {
  Component, OnInit, ChangeDetectionStrategy, ViewEncapsulation, AfterContentInit,
  ViewChild, ViewContainerRef, Input, ComponentRef, ComponentFactoryResolver, OnDestroy, ChangeDetectorRef
} from '@angular/core';
import {DynamicModalLoaderService} from '../../../services/dynamic-modal-loader.service';
import {ModalTypes} from '../contants/modal-types.enum';
import {DynamicModalAbstractComponent} from '../../../classes/dynamic-modal-abstract-component';
import {ModalManagementService} from "@ispm/layout/services/modal-management.service";
import { filter, takeUntil } from "rxjs/operators";
import { Subject } from 'rxjs';

// TODO: Called in each and every change detection cycle, changing it to onpush causing problem in popup screens
@Component({
  selector: 'gen-dynamic-modal-loader',
  templateUrl: './dynamic-modal-loader.component.html',
  styleUrls: ['./dynamic-modal-loader.component.scss', '../styles/modal.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
  host: {
    '(keydown)': 'onKeyDown($event)'
  }

})
export class DynamicModalLoaderComponent extends DynamicModalAbstractComponent implements OnInit, AfterContentInit, OnDestroy {
  private titleIcon: string;
  private focusableList: HTMLElement[] = [];
  private indexToFocus = -1;

  @ViewChild('target', {read: ViewContainerRef}) target;

  @Input() observableName: string = 'DYNAMIC_MODAL_LOADER';
  @Input() showOverlayBackground: boolean = true;
  public cmpRef: ComponentRef<any>;
  public isViewInitialized: boolean = false;
  public payload: any;
  public componentToLoad;
  public active: boolean = false;

  public title: string;
  public declineBtn: string;
  public approveBtn: string;
  public actionBtn: string;
  public classes: string;
  public containerClasses: string = '';
  public titleCss: string;
  public modalType: ModalTypes = ModalTypes.NONE;
  public typeText: string;
  public iconName: string;
  public showCloseIcon: boolean = true;
  public showEditIcon: boolean = false;
  public showMinimizeIcon = false;
  public hideHeader: boolean = false;
  public isMinimized = false;
  public modalObservableDestroyer$: Subject<any> = new Subject();
  private popStateEventHandler: (event: PopStateEvent) => void = undefined;

  constructor(private componentFactoryResolver: ComponentFactoryResolver,
              private _changeDetector: ChangeDetectorRef,
              private dynamicModalLoaderService: DynamicModalLoaderService,
              private modalManagementService: ModalManagementService) {
    super(dynamicModalLoaderService);

    this.popStateEventHandler = (event: PopStateEvent) => {
      // Handle the back button event here
      super.close(2, true);
    };
  }

  onNext(payload): void {
  }

  onError(error): void {
  }

  onComplete(): void {
  }

  ngOnInit() {
    /**
     * Subscribe to dynamic modal changes
     */
    this.createObservableSubscriptionInstance();
    window.addEventListener('popstate', this.popStateEventHandler);
  }

  createObservableSubscriptionInstance() {
    if (this.cmpRef) {
      this.cmpRef.instance.dismiss();
      this.dismissModal();
    }

    this.modalObservableDestroyer$.next();
    this.dynamicModalLoaderService.getObservable(this.observableName, true).pipe(
        takeUntil(this.modalObservableDestroyer$.asObservable()),
        filter((data) => !!data),
      )
      .subscribe(
        res => {
          this.componentToLoad = res['componentToLoad'];
          this.payload = res['payload'];
          this.modalType = res['modalType'];

          /**
           * In case the emitter wants to use specific observable name
           * the subscription is done here.
           * The default is the 'MAIN' observable.
           */
          if (this.componentToLoad != null) {
            // Dismiss previous popups if any of them are open.
            this.dismissModal();
            const content = this.payload && this.payload['content'] || '';
            this.title = this.payload && this.payload['title'] || '';
            this.backdropAllowed = this.payload && this.payload.hasOwnProperty('backdropAllowed') ? this.payload['backdropAllowed'] : true;
            this.approveBtn = this.payload && this.payload['approveBtn'] || '';
            this.declineBtn = this.payload && this.payload['declineBtn'] || '';
            this.actionBtn = this.payload && this.payload['actionBtn'] || '';
            this.classes = this.payload && this.payload['classes'] || '';
            this.titleIcon = this.payload && this.payload['titleIcon'] || '';
            if (this.setModalType(content ? this.title : '')) {
              this.title = '';
            }

            this.containerClasses = this.payload?.payload?.containerClasses || '';
            this.showMinimizeIcon = !!this.payload?.payload?.showMinimizeIcon;
            this.showEditIcon = this.payload && this.payload['payload'] && this.payload.payload.showEditIcon;
            this.hideHeader = this.payload && this.payload['payload'] && this.payload.payload['hideHeader'];
            this.titleCss = this.payload && this.payload['payload'] && this.payload.payload['titleCss'];
            this.showCloseIcon = this.backdropAllowed && !(this.declineBtn || (this.payload && this.payload.buttons && this.payload.buttons.declineBtn));

            // Renders new component to DOM and make child component to subscribe to this observable.
            this.renderComponent();
            this.callSubscriptionInChildComponent(this.observableName);
            this._changeDetector.markForCheck();
            this._changeDetector.detectChanges();
          }

        },
        err => null,
        () => {
          this.dismissModal();
          // While the observable dismiss, Create new instance of it
          // So other component will able to communicate with it
          // in the future.
          this.createObservableSubscriptionInstance();
        }
      );

  }

  private setModalType(title?: string): boolean {
    let classesToAdd = ' modal_with_type ';
    let changed = true;
    switch (this.modalType) {
      case ModalTypes.ALERT:
        this.typeText = title || 'Warning';
        classesToAdd += 'ALERT';
        this.iconName = 'icon-dls-warning-icon';
        break;
      case ModalTypes.WARNING:
          this.typeText = title;
          classesToAdd += 'ALERT';
          this.iconName = 'icon-dls-warning-icon';
          break;
      case ModalTypes.ERROR:
        this.typeText = title || 'Error';
        classesToAdd += 'ERROR';
        this.iconName = 'icon-dls-info';
        break;
      case ModalTypes.INFO:
        this.typeText = title || 'Info';
        classesToAdd += 'INFO';
        this.iconName = 'icon-dls-information';
        break;
      case ModalTypes.APPROVE:
        classesToAdd += "APPROVE";
        this.typeText = title || "Send approval";
        break;
      case ModalTypes.REJECT:
        classesToAdd += 'REJECT';
        this.typeText = 'Send rejection';
        break;
      case ModalTypes.SUCCESS:
        this.typeText = title || 'Success';
        classesToAdd += 'SUCCESS';
        break;
      case ModalTypes.CUSTOM:
        this.iconName = '';
        this.typeText = title;
        this.classes += 'custom__modal';
        classesToAdd += this.titleIcon;
        this.iconName = '';
        break;
      default:
        // typeText value is saved between modal popups, therefore we need to clear its value
        // GEN-31945
        this.typeText = null;
        changed = false;
        return;
    }
    this.classes += classesToAdd;
    return changed && !!title;
  }


  /**
   * Renders new component to DOM
   */
  renderComponent() {
    const factory = this.componentFactoryResolver.resolveComponentFactory(this.componentToLoad);
    this.cmpRef = this.target.createComponent(factory);
    this.active = true;
  }

  /**
   * Dismiss dynamic component by destroying it.
   */
  dismissModal() {
    if (this.cmpRef) {
      this.destroyModal();
      this.active = false;
      this.isMinimized = false;
      this._changeDetector.markForCheck();
    }
    this._changeDetector.detectChanges();
  }

  destroyModal() {

    // due to possible internal errors in destroy() method [see: GEN-53978], need to wrap it with try-catch to allow re-opening it later
    try {
      this.cmpRef.destroy();
    } catch (e) {
      console.log("error on cmpRef.destroy()" + e.message)
    }

    this.cmpRef = null;
  }

  /**
   * Child component must extend and implement subscribe and dismiss methods.
   * That's how the dynamicModalLoader communicates with child component
   * @param observableName
   */
  callSubscriptionInChildComponent(observableName: string) {
    this.cmpRef.instance.subscribe(observableName);
  }

  ngAfterContentInit(): void {
    this.isViewInitialized = true;
  }


  ngOnDestroy() {
    this._changeDetector.detach();
    if (this.cmpRef) {
      this.destroyModal();
    }

    this.modalObservableDestroyer$.next();
    this.modalObservableDestroyer$.complete();
    window.removeEventListener('popstate', this.popStateEventHandler);
  }

  get formFormClassName(): string {
    return this.payload && this.payload.payload ? 'Auto_Popup_Form_' + this.payload.payload.formId : '';
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      this.focusOnTab(this.cmpRef, event);
    }
  }

  focusOnTab(popup: any, event: KeyboardEvent) {
    let dialog = popup.location.nativeElement;
    this.focusableList = [];
    let focusableElements: NodeListOf<HTMLElement> = dialog.querySelectorAll('a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]');

    focusableElements.forEach((element: HTMLElement) => {
      if (element.offsetHeight > 0) this.focusableList.push(element);
    });
    this.focusableList.forEach((focusableInput: HTMLElement, index: number) => {
      if (focusableInput === event.target as HTMLElement) this.indexToFocus = index;
    });

    // indexToFocus will have the index of the input where TAB was pressed
    if (this.indexToFocus !== -1) {
      let nextIndexToFocus = this.indexToFocus < this.focusableList.length - 1 ? this.indexToFocus + 1 : 0;
      this.focusableList[nextIndexToFocus].focus();
    }
    event.stopPropagation();
    event.preventDefault();
  }

  closeModalLoader(data, isButton: boolean = true) {
    if (this.modalManagementService.discardDataOnClickOutside) {
      super.close(data, isButton);
    } else {
      this.modalManagementService.modalDataIsChanged().pipe(filter(changed => !changed)).subscribe(() => {
        super.close(data, isButton);
      });
    }
  }

  toggleMinimize() {
    this.isMinimized = !this.isMinimized;
  }
}
